import { PATIENT_REGISTRATION_FIELD_LIMITS } from '@/types/portal-api-field-limits.generated'
import { ValidationRule } from '@/hooks/useFormValidation'
import { isValidDate } from '@/utilities/dateValidation'

export { PATIENT_REGISTRATION_FIELD_LIMITS }
export { PCP_FAX_DIGIT_PATTERN, PCP_FIELD_LIMITS } from '@/types/portal-api-field-limits.generated'

// Generic validation rules that can be reused
export const genericValidationRules = {
  required: (fieldName: string) => (value: unknown) => {
    if (typeof value !== 'string' || value.trim() === '') return `${fieldName} is required`
    return undefined
  },
  maxLength: (fieldName: string, max: number) => (value: unknown) => {
    if (typeof value !== 'string' || value.trim().length === 0) return undefined
    return value.trim().length > max
      ? `Please enter a valid ${fieldName.toLowerCase()} (${max} characters or fewer).`
      : undefined
  },
  exactLength: (fieldName: string, length: number) => (value: unknown) => {
    if (typeof value !== 'string' || value.trim() === '') return undefined
    return value.trim().length === length
      ? undefined
      : `${fieldName} must be exactly ${length} characters`
  },
  pattern: (fieldName: string, pattern: RegExp, message: string) => (value: unknown) => {
    if (typeof value !== 'string' || value.trim() === '') return undefined
    return pattern.test(value) ? undefined : message
  },
  email: (value: unknown) => {
    const str = typeof value === 'string' ? value : ''
    if (!str) return undefined
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return !emailRegex.test(str) ? 'Please enter a valid email address' : undefined
  },
  phoneNumber: (value: unknown) => {
    const str = typeof value === 'string' ? value : ''
    if (!str) return 'Phone number is required'
    // US format is intentionally stricter than OpenAPI (min/max length only).
    const phoneRegex = /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
    if (!phoneRegex.test(str)) {
      return 'Please enter a valid phone number (e.g., +1 (123) 456-7890)'
    }
    if (str.length > PATIENT_REGISTRATION_FIELD_LIMITS.phone) {
      return `Please enter a valid phone number (${PATIENT_REGISTRATION_FIELD_LIMITS.phone} characters or fewer).`
    }
    return undefined
  },
  zipCode: (value: unknown) => {
    const str = typeof value === 'string' ? value : ''
    if (!str) return 'Zip code is required'
    // US ZIP is intentionally stricter than OpenAPI (maxLength only, no pattern).
    const zipRegex = /^\d{5}(-\d{4})?$/
    if (!zipRegex.test(str)) {
      return 'Please enter a valid zip code (e.g., 12345 or 12345-6789)'
    }
    if (str.length > PATIENT_REGISTRATION_FIELD_LIMITS.zipCode) {
      return 'Please enter a valid zip code (e.g., 12345 or 12345-6789)'
    }
    return undefined
  },
  dateOfBirth: (
    _value: unknown,
    data: { birthMonth?: unknown; birthDay?: unknown; birthYear?: unknown },
  ) => {
    const month = typeof data.birthMonth === 'string' ? data.birthMonth : ''
    const day = typeof data.birthDay === 'string' ? data.birthDay : ''
    const year = typeof data.birthYear === 'string' ? data.birthYear : ''
    if (!month || !day || !year) {
      return 'Date of birth is required'
    }
    if (!isValidDate(day, month, year)) {
      return 'Invalid date for the selected month and year'
    }
    return undefined
  },
}

// Personal Information validation rules
export const personalInfoValidationRules = <
  T extends Record<string, unknown>,
>(): ValidationRule<T>[] => [
  {
    field: 'firstName',
    validate: genericValidationRules.required('First name'),
  },
  {
    field: 'firstName',
    validate: genericValidationRules.maxLength(
      'First name',
      PATIENT_REGISTRATION_FIELD_LIMITS.firstName,
    ),
  },
  {
    field: 'lastName',
    validate: genericValidationRules.required('Last name'),
  },
  {
    field: 'lastName',
    validate: genericValidationRules.maxLength(
      'Last name',
      PATIENT_REGISTRATION_FIELD_LIMITS.lastName,
    ),
  },
  {
    field: 'phoneNumber',
    validate: genericValidationRules.phoneNumber,
  },
  {
    field: 'sexAssignedAtBirth',
    validate: genericValidationRules.required('Sex assigned at birth'),
  },
  {
    field: 'birthMonth',
    validate: genericValidationRules.dateOfBirth,
  },
  {
    field: 'birthDay',
    validate: genericValidationRules.dateOfBirth,
  },
  {
    field: 'birthYear',
    validate: genericValidationRules.dateOfBirth,
  },
]

// Address Information validation rules
export const addressInfoValidationRules = <
  T extends Record<string, unknown>,
>(): ValidationRule<T>[] => [
  {
    field: 'addressLine1',
    validate: genericValidationRules.required('Address line'),
  },
  {
    field: 'addressLine1',
    validate: genericValidationRules.maxLength(
      'Address line',
      PATIENT_REGISTRATION_FIELD_LIMITS.addressLine1,
    ),
  },
  {
    field: 'addressLine2',
    validate: genericValidationRules.maxLength(
      'Address line 2',
      PATIENT_REGISTRATION_FIELD_LIMITS.addressLine2,
    ),
  },
  {
    field: 'city',
    validate: genericValidationRules.required('City'),
  },
  {
    field: 'city',
    validate: genericValidationRules.maxLength('City', PATIENT_REGISTRATION_FIELD_LIMITS.city),
  },
  {
    field: 'state',
    validate: genericValidationRules.required('State'),
  },
  {
    field: 'state',
    validate: genericValidationRules.exactLength('State', PATIENT_REGISTRATION_FIELD_LIMITS.state),
  },
  {
    field: 'zipCode',
    validate: genericValidationRules.zipCode,
  },
]
